// Whether a Planner event occurrence is Upcoming or Passed (Jade, 2026-11-02).
//
// By default an occurrence is Passed once its date + time has gone by. Two marks,
// set by hand, override that for one occurrence:
//   - "Mark as passed": Passed now, even before its time. One-time events keep
//     using the event's own `passed` flag (the one the details panels always
//     set); a repeating event marks just that day, in `passedOn[date]`.
//   - "Keep in Upcoming": stays in Upcoming after its time has gone by, until
//     "Mark as passed" is tapped (`keepUpcomingOn[date]`).
// Moving an occurrence to a new date instead clears both marks, so it goes back
// to following its (new) date.
// Google Tasks synced in as events are not events here: their `passed` is their
// done tick (see googleTask.js), and callers leave them out.

export function isRecurringEvent(ev) {
  if (!ev) return false;
  const ft = ev.frequencyType;
  if (ft && ft !== "once") return true;
  return !!(ev.repeat && ev.repeat !== "None");
}

export function isMarkedPassed(ev, date) {
  if (!ev) return false;
  return isRecurringEvent(ev) ? !!(ev.passedOn && ev.passedOn[date]) : !!ev.passed;
}

export function isKeptUpcoming(ev, date) {
  return !!(ev && ev.keepUpcomingOn && ev.keepUpcomingOn[date]);
}

// Has this date + time gone by? An untimed (all-day) occurrence counts as gone
// only once its day is over.
export function isTimePast(date, time, now = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  if (date < today) return true;
  if (date > today) return false;
  if (!time) return false;
  const [hh, mm] = String(time).split(":").map(Number);
  return hh * 60 + mm < now.getHours() * 60 + now.getMinutes();
}

export function isEventPassed(ev, date, now = new Date()) {
  if (isKeptUpcoming(ev, date)) return false;
  if (isMarkedPassed(ev, date)) return true;
  return isTimePast(date, ev && ev.time, now);
}

const without = (map, key) => {
  if (!map || !(key in map)) return map;
  const next = { ...map };
  delete next[key];
  return Object.keys(next).length ? next : undefined;
};
const clean = (ev) => {
  const out = { ...ev };
  if (!out.passedOn) delete out.passedOn;
  if (!out.keepUpcomingOn) delete out.keepUpcomingOn;
  return out;
};

// "Mark as passed" (on = true) or taking that mark off (on = false).
export function withEventPassed(ev, date, on) {
  const keepUpcomingOn = without(ev.keepUpcomingOn, date);
  if (isRecurringEvent(ev)) {
    const passedOn = on ? { ...(ev.passedOn || {}), [date]: true } : without(ev.passedOn, date);
    return clean({ ...ev, passedOn, keepUpcomingOn });
  }
  return clean({ ...ev, passed: !!on, keepUpcomingOn });
}

// "Keep in Upcoming": out of Passed and kept in Upcoming on its own date.
export function withKeptUpcoming(ev, date) {
  const base = withEventPassed(ev, date, false);
  return clean({ ...base, keepUpcomingOn: { ...(base.keepUpcomingOn || {}), [date]: true } });
}

// The marks for one occurrence dropped, e.g. once it has been moved to a new date.
export function withoutEventMarks(ev, date) {
  const base = withEventPassed(ev, date, false);
  return clean({ ...base, keepUpcomingOn: without(base.keepUpcomingOn, date) });
}

// Moving the occurrence shown on `date` to `toDate`.
// One-time event: the event's own date changes. Repeating event: that one
// occurrence moves, through the same occurrence overrides a drag or Skip >
// Reschedule writes (`${id}:${originalDate}` -> { date }). Returns the new event
// and the new overrides map.
export function moveEventOccurrence(ev, date, toDate, overrides = {}) {
  const cleared = withoutEventMarks(ev, date);
  if (!isRecurringEvent(ev)) {
    return { event: { ...cleared, date: toDate }, overrides };
  }
  const prefix = `${ev.id}:`;
  // The occurrence may already have been moved here from another day.
  const movedKey = Object.keys(overrides).find(k => k.startsWith(prefix) && overrides[k] && overrides[k].date === date && k !== prefix + date);
  const key = movedKey || prefix + date;
  const next = { ...overrides, [key]: { ...(overrides[key] || {}), date: toDate } };
  delete next[key].skipped;
  return { event: cleared, overrides: next };
}

// The details panels' Mark as passed / Mark as upcoming button for an event.
// Not passed -> mark it passed. Marked passed by hand before its time -> just
// take the mark off (it's upcoming by its own date). Otherwise (its time has gone
// by) -> openUpcoming() shows the panel to move it or keep it in Upcoming.
export function eventStatusTap(ev, date, { setCalEvents, openUpcoming }) {
  const now = new Date();
  const set = (on) => setCalEvents(p => p.map(e => (e.id === ev.id ? withEventPassed(e, date, on) : e)));
  if (!isEventPassed(ev, date, now)) return set(true);
  if (isMarkedPassed(ev, date) && !isTimePast(date, ev.time, now)) return set(false);
  return openUpcoming();
}

// What the Mark as upcoming panel's two choices do.
export function applyKeepUpcoming(ev, date, setCalEvents) {
  setCalEvents(p => p.map(e => (e.id === ev.id ? withKeptUpcoming(e, date) : e)));
}
export function applyMoveEvent(ev, date, toDate, overrides, setCalEvents, setOverrides) {
  setCalEvents(p => p.map(e => (e.id === ev.id ? moveEventOccurrence(e, date, toDate, {}).event : e)));
  if (isRecurringEvent(ev) && setOverrides) setOverrides(moveEventOccurrence(ev, date, toDate, overrides || {}).overrides);
}
